import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import api from '../../lib/axios';
import Modal from '../../components/ui/Modal';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const DEFAULT_HOURS = DAYS.map((_, i) => ({
  day_of_week: i,
  enabled:     i >= 1 && i <= 6,
  start_time:  '10:00',
  end_time:    '20:00',
}));

export default function WorkingHoursModal({ open, member, onClose }) {
  const qc = useQueryClient();
  const [hours, setHours] = useState(DEFAULT_HOURS);

  const { data } = useQuery({
    queryKey: ['staff-hours', member?.id],
    queryFn:  () => api.get(`/staff/${member.id}/working-hours`).then(r => r.data.data),
    enabled:  open && !!member?.id,
  });

  useEffect(() => {
    if (!data) return;
    setHours(DEFAULT_HOURS.map(d => {
      const saved = data.find(h => h.day_of_week === d.day_of_week);
      return saved ? { ...d, enabled: true, start_time: saved.start_time, end_time: saved.end_time } : { ...d, enabled: false };
    }));
  }, [data]);

  const mut = useMutation({
    mutationFn: (payload) => api.put(`/staff/${member.id}/working-hours`, payload),
    onSuccess:  () => { qc.invalidateQueries(['staff']); qc.invalidateQueries(['staff-hours', member?.id]); onClose(); },
  });

  function toggle(i) {
    setHours(h => h.map((d, idx) => idx === i ? { ...d, enabled: !d.enabled } : d));
  }
  function setTime(i, field, val) {
    setHours(h => h.map((d, idx) => idx === i ? { ...d, [field]: val } : d));
  }

  function save() {
    const payload = hours.filter(d => d.enabled).map(({ day_of_week, start_time, end_time }) => ({ day_of_week, start_time, end_time }));
    mut.mutate(payload);
  }

  return (
    <Modal open={open} onClose={onClose} title={`Working hours — ${member?.name || ''}`} size="md">
      <div className="space-y-2">
        {hours.map((day, i) => (
          <div key={day.day_of_week}
            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors
              ${day.enabled ? 'border-primary/30 bg-primary/5' : 'border-surface-border bg-surface-50'}`}>
            <button type="button" onClick={() => toggle(i)}
              className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors shrink-0
                ${day.enabled ? 'bg-primary border-primary' : 'border-surface-200 bg-white'}`}>
              {day.enabled && <Check size={12} className="text-white" />}
            </button>
            <span className="text-sm font-medium text-ink w-24">{DAYS[i]}</span>
            {day.enabled ? (
              <div className="flex items-center gap-2 ml-auto">
                <input type="time" value={day.start_time}
                  onChange={e => setTime(i, 'start_time', e.target.value)}
                  className="input w-32 text-sm py-1.5" />
                <span className="text-ink-muted">–</span>
                <input type="time" value={day.end_time}
                  onChange={e => setTime(i, 'end_time', e.target.value)}
                  className="input w-32 text-sm py-1.5" />
              </div>
            ) : (
              <span className="ml-auto text-xs text-ink-muted">Closed</span>
            )}
          </div>
        ))}
      </div>

      {mut.isError && <p className="text-xs text-red-500 mt-3">{mut.error?.response?.data?.error?.message || 'Save failed'}</p>}

      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-surface-border">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={save} disabled={mut.isPending} className="btn-primary">
          {mut.isPending ? 'Saving…' : 'Save hours'}
        </button>
      </div>
    </Modal>
  );
}
